import { useEffect, useState } from "react";
import { config } from "../config";
import { devices as deviceList } from "../devices";
import type { AppState, DeviceState } from "../types";

// The dynamic, per-device fields the backend tracks. Static device metadata
// (name, alarm config) lives in src/devices.ts and is merged in below.
type DeviceDynamic = Pick<
  DeviceState,
  "status" | "statusReason" | "lastTelemTs" | "telemetry"
>;

interface Snapshot {
  devices: Record<string, DeviceDynamic>;
}

function emptyDynamic(): DeviceDynamic {
  return {
    status: "unknown",
    statusReason: null,
    lastTelemTs: null,
    telemetry: null,
  };
}

function buildState(
  snapshot: Snapshot,
  connected: boolean,
  lastError: string | null,
): AppState {
  const devices: Record<string, DeviceState> = {};
  for (const meta of deviceList) {
    const dyn = snapshot.devices[meta.id] ?? emptyDynamic();
    devices[meta.id] = { meta, ...dyn };
  }
  return { connected, lastError, devices };
}

export function useIot(): { state: AppState } {
  const [state, setState] = useState<AppState>(() =>
    buildState({ devices: {} }, false, null),
  );

  useEffect(() => {
    const source = new EventSource(`${config.apiBase}/stream`);

    source.onopen = () => {
      setState((prev) => ({ ...prev, connected: true, lastError: null }));
    };
    source.onmessage = (ev) => {
      try {
        const snapshot = JSON.parse(ev.data) as Snapshot;
        setState(buildState(snapshot, true, null));
      } catch {
        // ignore malformed frame
      }
    };
    source.onerror = () => {
      // EventSource reconnects on its own; surface the gap in the UI.
      setState((prev) => ({
        ...prev,
        connected: false,
        lastError: "Stream disconnected — reconnecting…",
      }));
    };

    return () => source.close();
  }, []);

  return { state };
}
